"use client";

import { PieChart, X } from "lucide-react";
import { usePrenotaStore } from "@PN/inclusao";
import { Button, Badge, Separator } from "visuais";
import { RateioTable } from "./RateioTable";
import { useRateio } from "./useRateio";
import {
  ReactNode,
  memo,
  useMemo,
  useRef,
  useState,
  useEffect,
  useId,
} from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useOutsideClick } from "hooks";

interface RateioDialogProps {
  className?: string;
  trigger?: ReactNode;
  noTrigger?: boolean;
}

// Componente do conteúdo do dialog com animações Aceternity
const DialogContentComponent = memo(() => {
  const rateios = usePrenotaStore((state) => state.draft.rateios);
  const { calculations, temRegrasEspeciais } = useRateio();
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="flex flex-col h-full bg-popover border shadow-2xl rounded-lg overflow-hidden">
      {/* Header */}
      <motion.div
        className="flex items-center gap-2 p-6 border-b"
        initial={shouldReduceMotion ? undefined : { opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: shouldReduceMotion ? 0 : 0.05,
          duration: shouldReduceMotion ? 0.1 : 0.15,
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "tween",
        }}
        style={{ willChange: "transform, opacity" }}
      >
        <motion.div
          animate={
            shouldReduceMotion
              ? {}
              : {
                  scale: [1, 1.05, 1],
                }
          }
          transition={{
            duration: shouldReduceMotion ? 0 : 1,
            repeat: shouldReduceMotion ? 0 : 1, // ✅ Apenas 1 vez, não infinito
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          style={{ willChange: shouldReduceMotion ? "auto" : "transform" }}
        >
          <PieChart className="h-5 w-5" />
        </motion.div>
        <span className="text-base font-semibold">Configurar Rateio</span>
      </motion.div>

      {/* Content */}
      <motion.div
        className="flex-1 flex flex-col min-h-0 px-4"
        initial={shouldReduceMotion ? undefined : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          delay: shouldReduceMotion ? 0 : 0.1,
          duration: shouldReduceMotion ? 0.1 : 0.15,
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "tween",
        }}
        style={{ willChange: "transform, opacity" }}
      >
        <RateioTable />
      </motion.div>

      {/* Footer */}
      <motion.div
        className="border-t bg-muted/30 p-4 mt-2"
        initial={shouldReduceMotion ? undefined : { opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: shouldReduceMotion ? 0 : 0.15,
          duration: shouldReduceMotion ? 0.1 : 0.15,
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "tween",
        }}
        style={{ willChange: "transform, opacity" }}
      >
        <div className="flex flex-wrap justify-between items-center gap-4 text-sm">
          {temRegrasEspeciais && rateios.length === 0 && (
            <motion.div
              className="flex gap-2 items-center h-12"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <motion.div
                className="text-center text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 rounded-md border border-amber-200 dark:border-amber-800"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
              >
                ⚠️ Tipos com regras especiais requerem exatamente 1 rateio de
                100%
              </motion.div>
              <Separator
                orientation="vertical"
                className="bg-amber-200 dark:bg-amber-800"
              />
            </motion.div>
          )}

          <motion.div
            className="flex gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <motion.div
              className="text-center"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              <div className="text-muted-foreground">Total da NF</div>
              <motion.div
                className="font-semibold text-base"
                animate={{
                  scale: [1, 1.02, 1],
                  transition: {
                    duration: 1,
                    repeat: 0, // ✅ Não repetir infinitamente
                    ease: "easeInOut",
                  },
                }}
              >
                R$ {calculations.totalGeral.toFixed(2).replace(".", ",")}
              </motion.div>
            </motion.div>
            <motion.div
              className="text-center"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              <div className="text-muted-foreground">Valor Rateado</div>
              <motion.div
                className="font-semibold text-base text-blue-600"
                animate={{
                  scale: [1, 1.02, 1],
                  transition: {
                    duration: 1,
                    repeat: 0, // ✅ Não repetir infinitamente
                    ease: "easeInOut",
                    delay: 0.2,
                  },
                }}
              >
                R$ {calculations.totalRateado.toFixed(2).replace(".", ",")}
              </motion.div>
            </motion.div>
            <motion.div
              className="text-center"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              <div className="text-muted-foreground">Percentual</div>
              <motion.div
                className="font-semibold text-base text-blue-600"
                animate={{
                  scale: [1, 1.02, 1],
                  transition: {
                    duration: 1,
                    repeat: 0, // ✅ Não repetir infinitamente
                    ease: "easeInOut",
                    delay: 0.4,
                  },
                }}
              >
                {calculations.percentualRateado.toFixed(1)}%
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05 }}
          >
            <div className="text-muted-foreground">Valor Restante</div>
            <motion.div
              className={`font-semibold text-base ${
                calculations.restante < 0.01
                  ? "text-green-600"
                  : calculations.restante < 0
                  ? "text-red-600"
                  : "text-orange-600"
              }`}
              animate={{
                scale: [1, 1.02, 1],
                transition: {
                  duration: 1,
                  repeat: 0, // ✅ Não repetir infinitamente
                  ease: "easeInOut",
                  delay: 0.6,
                },
              }}
            >
              R$ {calculations.restante.toFixed(2).replace(".", ",")}
            </motion.div>
          </motion.div>

          {calculations.isCompleto && calculations.totalGeral > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: -5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
              >
                <Badge
                  variant="default"
                  className={`text-sm py-1 px-3 ${
                    temRegrasEspeciais
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  <motion.span
                    animate={{
                      scale: [1, 1.1, 1],
                      transition: {
                        duration: 1,
                        repeat: 0, // ✅ Não repetir infinitamente
                        ease: "easeInOut",
                      },
                    }}
                  >
                    ✓{" "}
                    {temRegrasEspeciais
                      ? "Regras Especiais 100%"
                      : "Rateio Completo"}
                  </motion.span>
                </Badge>
              </motion.div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
});

DialogContentComponent.displayName = "DialogContentComponent";

export const RateioDialog = ({
  className = "",
  trigger,
  noTrigger = false,
}: RateioDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const shouldReduceMotion = useReducedMotion();

  // ✅ Detecção adicional de hardware limitado
  const [isLowEndDevice, setIsLowEndDevice] = useState(false);

  useEffect(() => {
    // Detecta dispositivos com hardware limitado
    const checkLowEndDevice = () => {
      const isLowEnd =
        navigator.hardwareConcurrency <= 2 || // CPU cores
        window.innerWidth < 1024; // Resolução baixa

      setIsLowEndDevice(isLowEnd);
    };

    checkLowEndDevice();
    window.addEventListener("resize", checkLowEndDevice);

    return () => window.removeEventListener("resize", checkLowEndDevice);
  }, []);

  const rateios = usePrenotaStore((state) => state.draft.rateios);
  const produtos = usePrenotaStore((state) => state.draft.produtos);
  const { calculations } = useRateio();

  const hasProdutos = produtos.length > 0 || calculations.totalGeral > 0;
  const btnLabel = "Rateio";

  // ✅ Usa detecção combinada para otimização máxima
  const shouldOptimize = shouldReduceMotion || isLowEndDevice;

  // Função para calcular a transformação do trigger
  const getTriggerTransform = () => {
    if (!triggerRef.current) {
      return {
        x: 0,
        y: 0,
        width: 200,
        height: 50,
      };
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const windowCenterX = window.innerWidth / 2;
    const windowCenterY = window.innerHeight / 2;

    const offsetX = centerX - windowCenterX;
    const offsetY = centerY - windowCenterY;

    return {
      x: offsetX,
      y: offsetY,
      width: rect.width,
      height: rect.height,
    };
  };

  // Estado para capturar a transformação do trigger quando o dialog abre
  const [triggerTransform, setTriggerTransform] = useState(() =>
    getTriggerTransform()
  );

  // Atualiza a transformação quando o dialog abre
  useEffect(() => {
    if (isOpen) {
      const transform = getTriggerTransform();
      setTriggerTransform(transform);

      // Esconde o trigger button durante a animação
      if (triggerRef.current) {
        triggerRef.current.style.opacity = "0";
        triggerRef.current.style.pointerEvents = "none";
      }
    } else {
      // Restaura o trigger button quando fecha
      if (triggerRef.current) {
        triggerRef.current.style.opacity = "1";
        triggerRef.current.style.pointerEvents = "auto";
      }
    }
  }, [isOpen]);

  // Previne scroll do body quando dialog está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Fecha o dialog quando clicar fora (ignorando dropdowns)
  useOutsideClick(dialogRef as React.RefObject<HTMLElement>, (event) => {
    if (isOpen) {
      // Verifica se o clique foi em um elemento de dropdown
      const target = event.target as HTMLElement;
      const isDropdownClick =
        target.closest("[data-radix-popper-content-wrapper]") ||
        target.closest("[data-radix-select-content]") ||
        target.closest("[data-radix-combobox-content]") ||
        target.closest(".combobox-dropdown") ||
        target.closest('[role="listbox"]') ||
        target.closest('[role="option"]');

      if (!isDropdownClick) {
        console.log("🔥 [Dialog] Fechando por clique fora");
        setIsOpen(false);
      }
    }
  });

  // Fecha com ESC
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && isOpen) {
        console.log("🔥 [Dialog] Fechando por ESC");
        setIsOpen(false);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const defaultTrigger = useMemo(
    () => (
      <motion.div
        layoutId={shouldOptimize ? undefined : `rateio-trigger-${id}`}
        style={{ willChange: shouldOptimize ? "auto" : "transform" }}
      >
        <Button
          ref={triggerRef}
          variant="outline"
          className={`flex justify-between w-full relative ${className}`}
          title={
            !hasProdutos
              ? "Adicione produtos primeiro para configurar o rateio"
              : undefined
          }
          onClick={() => setIsOpen(true)}
          disabled={!hasProdutos}
        >
          <span className="truncate">{btnLabel}</span>
          {rateios.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {rateios.length}
            </Badge>
          )}
          <PieChart className="h-4 w-4" />
        </Button>
      </motion.div>
    ),
    [className, hasProdutos, btnLabel, rateios.length, id, shouldOptimize]
  );

  if (noTrigger) {
    return <DialogContentComponent />;
  }

  return (
    <div className="col-span-3 space-y-2 flex flex-col">
      {trigger || defaultTrigger}

      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={shouldOptimize ? undefined : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={shouldOptimize ? undefined : { opacity: 0 }}
            transition={{
              duration: shouldOptimize ? 0.05 : 0.1,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="fixed inset-0 bg-black/20 backdrop-blur-xs z-[10]"
            onClick={(e) => {
              // Verifica se o clique foi em um elemento de dropdown
              const target = e.target as HTMLElement;
              const isDropdownClick =
                target.closest("[data-radix-popper-content-wrapper]") ||
                target.closest("[data-radix-select-content]") ||
                target.closest("[data-radix-combobox-content]") ||
                target.closest(".combobox-dropdown") ||
                target.closest('[role="listbox"]') ||
                target.closest('[role="option"]');

              if (!isDropdownClick) {
                setIsOpen(false);
              }
            }}
            style={{ willChange: "opacity" }}
          />
        )}
      </AnimatePresence>

      {/* Dialog Content */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 grid place-items-center z-[20] rounded-lg">
            <motion.div
              ref={dialogRef}
              layoutId={shouldReduceMotion ? undefined : `rateio-trigger-${id}`}
              initial={
                shouldReduceMotion
                  ? false
                  : {
                      width: triggerTransform.width,
                      height: triggerTransform.height,
                      x: triggerTransform.x,
                      y: triggerTransform.y,
                      borderRadius: "8px",
                    }
              }
              animate={{
                width: "60vw",
                height: "95vh",
                x: 0,
                y: 0,
                borderRadius: "8px",
              }}
              exit={
                shouldReduceMotion
                  ? undefined
                  : {
                      width: triggerTransform.width,
                      height: triggerTransform.height,
                      x: triggerTransform.x,
                      y: triggerTransform.y,
                      borderRadius: "8px",
                    }
              }
              transition={{
                duration: shouldReduceMotion ? 0.1 : 0.2,
                ease: [0.25, 0.46, 0.45, 0.94],
                type: "tween",
              }}
              style={{
                willChange: shouldReduceMotion
                  ? "auto"
                  : "transform, width, height",
                transform: "translateZ(0)",
              }}
              className="bg-popover border shadow-2xl flex flex-col rounded-lg"
            >
              {/* Close Button */}
              <motion.button
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
                onClick={() => {
                  console.log("🔥 [Dialog] Botão X clicado");
                  setIsOpen(false);
                }}
                whileHover={shouldReduceMotion ? {} : { scale: 1.05 }}
                whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                initial={
                  shouldReduceMotion ? undefined : { opacity: 0, scale: 0 }
                }
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: shouldReduceMotion ? 0 : 0.05,
                  duration: shouldReduceMotion ? 0.05 : 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                style={{
                  willChange: shouldReduceMotion
                    ? "auto"
                    : "transform, opacity",
                }}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Fechar</span>
              </motion.button>

              {/* Content */}
              <motion.div
                className="flex-1 w-full min-h-0 flex flex-col"
                initial={shouldReduceMotion ? undefined : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={shouldReduceMotion ? undefined : { opacity: 0 }}
                transition={{
                  delay: shouldReduceMotion ? 0 : 0.05,
                  duration: shouldReduceMotion ? 0.05 : 0.1,
                }}
                style={{
                  height: "100%",
                  maxHeight: "100%",
                  willChange: "opacity",
                }}
              >
                <DialogContentComponent />
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
